package com.trevorwiebe.timesheet.mileage.presentation

import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.UserRole
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDateTimePicker
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.mileage.domain.US_STATES
import com.trevorwiebe.timesheet.mileage.domain.UsState
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.LocationType
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.TripType
import com.trevorwiebe.timesheet.mileage.domain.model.TripTypeData
import com.trevorwiebe.timesheet.mileage.domain.model.EquipmentType
import kotlin.time.ExperimentalTime

@Composable
expect fun AddMileageEntryDialog(uiState: MileageUiState, onEvent: (MileageEvent) -> Unit)

@OptIn(ExperimentalMaterial3Api::class, ExperimentalTime::class, ExperimentalLayoutApi::class)
@Composable
internal fun AddMileageEntryFormContent(uiState: MileageUiState, onEvent: (MileageEvent) -> Unit) {
    val form = uiState.addEntryForm
    val currentUser = uiState.currentUser ?: return
    val isElevated = currentUser.role == UserRole.ADMIN || currentUser.role == UserRole.DISPATCHER
    val trucks = uiState.equipment.filter { it.type == EquipmentType.TRUCK && it.active }
    val trailers = uiState.equipment.filter { it.type == EquipmentType.TRAILER && it.active }
    val commonStates = US_STATES.filter { it.common }.sortedBy { it.name }
    val uncommonStates = US_STATES.filterNot { it.common }.sortedBy { it.name }

    val isReadOnly = uiState.isEditingLocked

    fun update(newForm: AddMileageFormState) {
        if (!isReadOnly) onEvent(MileageEvent.OnUpdateForm(newForm))
    }

    val firstTripFieldFocus = remember { FocusRequester() }
    val focusManager = LocalFocusManager.current
    val hasTripTextFields = when (form.selectedTripType) {
        TripType.FUEL -> true
        TripType.SERVICE -> true
        TripType.OTHER -> true
        TripType.START_END -> true
        TripType.LOADING, TripType.UNLOADING -> true
        TripType.STATE_LINE -> true
    }
    val onSave = { if (!isReadOnly) buildAndSubmit(form, currentUser.id, uiState.editingEntry?.id, uiState.hasFuelReceipt, onEvent) }

    Column(
        modifier = Modifier
            .responsiveDialogWidth()
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 16.dp)
    ) {
        Text(
            text = if (uiState.editingEntry != null) "Edit Mileage Entry" else "Add Mileage Entry",
            style = MaterialTheme.typography.titleLarge
        )

        // Date row
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "Date: ${Util.toFriendlyDateTime(form.date)}",
                style = MaterialTheme.typography.bodyLarge,
                modifier = Modifier.weight(1f)
            )
            if (!isReadOnly) {
                TextButton(
                    onClick = { update(form.copy(showDatePicker = true)) },
                    colors = ButtonDefaults.buttonColors(
                        contentColor = MaterialTheme.colorScheme.tertiary,
                        containerColor = MaterialTheme.colorScheme.background
                    )
                ) {
                    Text("Change")
                }
            }
        }

        // Trip type selector
        SectionLabel("Trip Type")
        FlowRow(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy((-8).dp)
        ) {
            uiState.tripTypeOptions.forEach { option ->
                FilterChip(
                    selected = form.selectedTripTypeOption == option,
                    onClick = { update(form.copy(selectedTripTypeOption = option)) },
                    label = { Text(option.displayLabel()) },
                    modifier = Modifier.padding(end = 4.dp),
                    border = FilterChipDefaults.filterChipBorder(
                        enabled = true,
                        borderColor = MaterialTheme.colorScheme.tertiary,
                        selected = form.selectedTripTypeOption == option,
                    ),
                    colors = FilterChipDefaults.filterChipColors(
                        containerColor = MaterialTheme.colorScheme.background,
                        selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                        selectedLabelColor = MaterialTheme.colorScheme.onTertiary
                    )
                )
            }
        }

        // Trip details
        SectionLabel("Trip Details")
        TimesheetTextField(
            text = form.mileageText,
            placeholder = "Odometer reading",
            readOnly = isReadOnly,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Number,
                imeAction = if (hasTripTextFields) ImeAction.Next else ImeAction.Done
            ),
            keyboardActions = KeyboardActions(
                onNext = { firstTripFieldFocus.requestFocus() },
                onDone = { focusManager.clearFocus(); onSave() }
            ),
            onTextChange = {
                onEvent(MileageEvent.OnSetOdometerError(null))
                update(form.copy(mileageText = it))
            }
        )
        uiState.odometerError?.let { error ->
            Spacer(Modifier.height(4.dp))
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall
            )
        }

        // Employee dropdown (admin/dispatcher only)
        if (isElevated && uiState.userList.isNotEmpty()) {
            Spacer(Modifier.height(8.dp))
            SelectionDropdown(
                label = "Employee",
                items = uiState.userList,
                selectedId = form.selectedEmployeeId,
                itemId = { it.id },
                itemLabel = { it.name },
                onSelect = { update(form.copy(selectedEmployeeId = it ?: "")) },
                enabled = !isReadOnly
            )
        }

        // Truck dropdown
        Spacer(Modifier.height(8.dp))
        SelectionDropdown(
            label = "Truck",
            items = trucks,
            selectedId = form.selectedEquipmentId,
            itemId = { it.id },
            itemLabel = { it.unitNumber },
            onSelect = { update(form.copy(selectedEquipmentId = it ?: "", equipmentError = null)) },
            enabled = !isReadOnly
        )
        form.equipmentError?.let { error ->
            Spacer(Modifier.height(4.dp))
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall
            )
        }

        // Trailer dropdown
        Spacer(Modifier.height(8.dp))
        SelectionDropdown(
            label = "Trailer",
            items = trailers,
            selectedId = form.selectedTrailerId,
            itemId = { it.id },
            itemLabel = { it.unitNumber },
            onSelect = { update(form.copy(selectedTrailerId = it)) },
            includeNoneOption = true,
            enabled = !isReadOnly
        )

        // Dynamic fields per trip type (each adds its own section header)
        when (form.selectedTripType) {
            TripType.STATE_LINE -> StateLineFields(
                form = form,
                commonStates = commonStates,
                uncommonStates = uncommonStates,
                onUpdate = ::update,
                firstFieldFocus = firstTripFieldFocus,
                onSave = onSave,
                fromStateError = form.fromStateError,
                toStateError = form.toStateError,
                readOnly = isReadOnly
            )
            TripType.FUEL -> FuelFields(
                uiState = uiState,
                onEvent = onEvent,
                form = form,
                locations = uiState.locations.filter { it.type == LocationType.FUEL && !it.deleted },
                onUpdate = ::update,
                firstFieldFocus = firstTripFieldFocus,
                onSave = onSave,
                locationError = form.locationError,
                readOnly = isReadOnly
            )
            TripType.LOADING, TripType.UNLOADING -> LoadingUnloadingFields(
                form = form,
                locations = uiState.locations.filter { it.type == LocationType.DELIVERY && !it.deleted },
                onUpdate = ::update,
                firstFieldFocus = firstTripFieldFocus,
                onSave = onSave,
                locationError = form.locationError,
                readOnly = isReadOnly
            )
            TripType.SERVICE -> ServiceFields(
                form = form,
                onUpdate = ::update,
                firstFieldFocus = firstTripFieldFocus,
                onSave = onSave,
                serviceLocationError = form.serviceLocationError,
                readOnly = isReadOnly
            )
            TripType.OTHER -> OtherFields(
                form = form,
                locations = uiState.locations.filter { !it.deleted },
                onUpdate = ::update,
                firstFieldFocus = firstTripFieldFocus,
                onSave = onSave,
                readOnly = isReadOnly
            )
            TripType.START_END -> StartEndFields(
                form = form,
                onUpdate = ::update,
                firstFieldFocus = firstTripFieldFocus,
                onSave = onSave,
                readOnly = isReadOnly
            )
        }

        Spacer(Modifier.height(8.dp))

        DialogButtonRow(
            confirmButtonText = if (isReadOnly) null else if (uiState.isSaving) "Saving…" else "Save",
            onConfirmClick = onSave,
            confirmEnabled = !uiState.isSaving,
            dismissButtonText = if (isReadOnly) "Close" else "Cancel",
            onDismissClick = { onEvent(MileageEvent.OnDismissAddDialog) },
            neutralButtonText = if (!isReadOnly && uiState.editingEntry != null) "Delete" else null,
            onNeutralClick = { uiState.editingEntry?.let { onEvent(MileageEvent.OnRequestDelete(it)) } }
        )

        Spacer(Modifier.height(8.dp))
    }

    NativeDateTimePicker(
        dateTime = form.date,
        showPicker = form.showDatePicker,
        onDismiss = { update(form.copy(showDatePicker = false)) },
        onDateTimeSelected = { newDateTime ->
            onEvent(MileageEvent.OnSetOdometerError(null))
            update(form.copy(date = newDateTime, showDatePicker = false))
        }
    )
}

@OptIn(ExperimentalTime::class)
internal fun buildAndSubmit(
    form: AddMileageFormState,
    currentUserId: String,
    editingEntryId: String?,
    hasReceipt: Boolean,
    onEvent: (MileageEvent) -> Unit
) {
    val mileage = form.mileageText.toLongOrNull()
    if (mileage == null) {
        onEvent(MileageEvent.OnSetOdometerError("Enter a valid odometer reading."))
        return
    }

    val isLocationTrip = form.selectedTripType == TripType.FUEL ||
        form.selectedTripType == TripType.LOADING ||
        form.selectedTripType == TripType.UNLOADING

    val equipmentError = if (form.selectedEquipmentId.isEmpty()) "Select a truck." else null
    val fromStateError = if (form.selectedTripType == TripType.STATE_LINE && form.fromState.isEmpty()) "Select a from state." else null
    val toStateError = if (form.selectedTripType == TripType.STATE_LINE && form.toState.isEmpty()) "Select a to state." else null
    val locationError = when {
        isLocationTrip && !form.isNewLocation && form.selectedLocationId.isEmpty() -> "Select a location."
        isLocationTrip && form.isNewLocation && form.newLocationName.isEmpty() ->
            if (form.selectedTripType == TripType.FUEL) "Enter a fuel vendor." else "Enter a location name."
        else -> null
    }
    val receiptError = if (form.selectedTripType == TripType.FUEL && !hasReceipt) "Add a receipt photo." else null
    val hasErrors = listOf(equipmentError, fromStateError, toStateError, locationError, receiptError).any { it != null }
    if (hasErrors) {
        onEvent(MileageEvent.OnUpdateForm(form.copy(
            equipmentError = equipmentError,
            fromStateError = fromStateError,
            toStateError = toStateError,
            locationError = locationError,
            receiptError = receiptError
        )))
        return
    }

    val tripTypeData: TripTypeData = when (form.selectedTripType) {
        TripType.STATE_LINE -> TripTypeData.StateLine(form.fromState, form.toState, form.highwayText.trim())
        TripType.FUEL -> TripTypeData.Fuel(
            locationId = form.selectedLocationId,
            gallons = form.gallonsText.toDoubleOrNull() ?: 0.0,
            receiptImageUrl = null
        )
        TripType.LOADING -> TripTypeData.Loading(form.selectedLocationId, form.loadingUnloadingNotesText.trim())
        TripType.UNLOADING -> TripTypeData.Unloading(form.selectedLocationId, form.loadingUnloadingNotesText.trim())
        TripType.SERVICE -> TripTypeData.Service(form.serviceLocationText)
        TripType.OTHER -> TripTypeData.Other(form.otherNotesText, form.selectedLocationId.ifEmpty { null })
        TripType.START_END -> TripTypeData.StartEnd(form.startEndLocationText)
    }

    val entry = MileageEntry(
        id = editingEntryId ?: "",
        date = form.date,
        employeeId = form.selectedEmployeeId.ifEmpty { currentUserId },
        equipmentId = form.selectedEquipmentId,
        trailerId = form.selectedTrailerId,
        mileageValue = mileage,
        tripType = form.selectedTripType,
        tripTypeData = tripTypeData
    )
    val newLocation: Location? = if (form.isNewLocation && form.newLocationName.isNotEmpty()) {
        Location(
            id = "",
            name = form.newLocationName,
            city = form.newLocationCity,
            state = form.newLocationState,
            type = if (form.selectedTripType == TripType.FUEL) LocationType.FUEL else LocationType.DELIVERY
        )
    } else null

    if (editingEntryId != null) {
        onEvent(MileageEvent.OnUpdateEntry(entry, newLocation))
    } else {
        onEvent(MileageEvent.OnSaveEntry(entry, newLocation))
    }
}

// ── Private dropdown composables ─────────────────────────────────────────────

/** Section header (16.dp top padding + label) used to group the form like the iOS sheet. */
@Composable
private fun SectionLabel(text: String) {
    Spacer(Modifier.height(16.dp))
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
    Spacer(Modifier.height(4.dp))
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun <T> SelectionDropdown(
    label: String,
    items: List<T>,
    selectedId: String?,
    itemId: (T) -> String,
    itemLabel: (T) -> String,
    onSelect: (String?) -> Unit,
    includeNoneOption: Boolean = false,
    enabled: Boolean = true
) {
    var expanded by remember { mutableStateOf(false) }
    val selectedLabel = items.firstOrNull { itemId(it) == selectedId }?.let { itemLabel(it) }
        ?: if (includeNoneOption) "None" else ""
    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { if (enabled) expanded = it }
    ) {
        TimesheetTextField(
            text = selectedLabel,
            label = label,
            readOnly = true,
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded) },
            modifier = Modifier.menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable)
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = MaterialTheme.colorScheme.background
        ) {
            if (includeNoneOption) {
                DropdownMenuItem(
                    text = { Text("None") },
                    onClick = { onSelect(null); expanded = false }
                )
            }
            items.forEach { item ->
                DropdownMenuItem(
                    text = { Text(itemLabel(item)) },
                    onClick = { onSelect(itemId(item)); expanded = false },
                )
            }
        }
    }
}

// ── Private field composables ─────────────────────────────────────────────────

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun StateLineFields(
    form: AddMileageFormState,
    commonStates: List<UsState>,
    uncommonStates: List<UsState>,
    onUpdate: (AddMileageFormState) -> Unit,
    firstFieldFocus: FocusRequester,
    onSave: () -> Unit,
    fromStateError: String? = null,
    toStateError: String? = null,
    readOnly: Boolean = false
) {
    val focusManager = LocalFocusManager.current
    val allStates = commonStates + uncommonStates
    val fromStateName = allStates.firstOrNull { it.code == form.fromState }?.name ?: ""
    val toStateName = allStates.firstOrNull { it.code == form.toState }?.name ?: ""

    SectionLabel("States")
    StateDropdown(
        label = "From State",
        selectedName = fromStateName,
        commonStates = commonStates,
        uncommonStates = uncommonStates,
        onSelect = { code -> onUpdate(form.copy(fromState = code, fromStateError = null)) },
        enabled = !readOnly
    )
    fromStateError?.let { error ->
        Spacer(Modifier.height(4.dp))
        Text(
            text = error,
            color = MaterialTheme.colorScheme.error,
            style = MaterialTheme.typography.bodySmall
        )
    }
    Spacer(Modifier.height(8.dp))
    StateDropdown(
        label = "To State",
        selectedName = toStateName,
        commonStates = commonStates,
        uncommonStates = uncommonStates,
        onSelect = { code -> onUpdate(form.copy(toState = code, toStateError = null)) },
        enabled = !readOnly
    )
    toStateError?.let { error ->
        Spacer(Modifier.height(4.dp))
        Text(
            text = error,
            color = MaterialTheme.colorScheme.error,
            style = MaterialTheme.typography.bodySmall
        )
    }
    SectionLabel("Highway (optional)")
    TimesheetTextField(
        modifier = Modifier.focusRequester(firstFieldFocus),
        text = form.highwayText,
        placeholder = "Highway name",
        readOnly = readOnly,
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Characters,
            imeAction = ImeAction.Done
        ),
        keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus(); onSave() }),
        onTextChange = { onUpdate(form.copy(highwayText = it)) }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun StateDropdown(
    label: String,
    selectedName: String,
    commonStates: List<UsState>,
    uncommonStates: List<UsState>,
    onSelect: (String) -> Unit,
    enabled: Boolean = true
) {
    var expanded by remember { mutableStateOf(false) }
    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { if (enabled) expanded = it }
    ) {
        TimesheetTextField(
            text = selectedName,
            label = label,
            readOnly = true,
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded) },
            modifier = Modifier.menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable)
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = MaterialTheme.colorScheme.background
        ) {
            if (commonStates.isNotEmpty()) {
                DropdownMenuItem(
                    text = {
                        Text(
                            text = "Common",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.tertiary
                        )
                    },
                    onClick = {},
                    enabled = false
                )
                commonStates.forEach { state ->
                    DropdownMenuItem(
                        text = { Text(state.name) },
                        onClick = { onSelect(state.code); expanded = false }
                    )
                }
            }
            if (uncommonStates.isNotEmpty()) {
                DropdownMenuItem(
                    text = {
                        Text(
                            text = "Other",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.tertiary
                        )
                    },
                    onClick = {},
                    enabled = false
                )
                uncommonStates.forEach { state ->
                    DropdownMenuItem(
                        text = { Text(state.name) },
                        onClick = { onSelect(state.code); expanded = false }
                    )
                }
            }
        }
    }
}

@Composable
private fun FuelFields(
    uiState: MileageUiState,
    onEvent: (MileageEvent) -> Unit,
    form: AddMileageFormState,
    locations: List<Location>,
    onUpdate: (AddMileageFormState) -> Unit,
    firstFieldFocus: FocusRequester,
    onSave: () -> Unit,
    locationError: String? = null,
    readOnly: Boolean = false
) {
    val focusManager = LocalFocusManager.current

    LocationPickerFields(
        form = form,
        locations = locations,
        nameFieldLabel = "Fuel vendor",
        onUpdate = onUpdate,
        locationError = locationError,
        readOnly = readOnly
    )
    SectionLabel("Fuel Details")
    TimesheetTextField(
        modifier = Modifier.focusRequester(firstFieldFocus),
        text = form.gallonsText,
        placeholder = "Gallons",
        readOnly = readOnly,
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal, imeAction = ImeAction.Done),
        keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus(); onSave() }),
        onTextChange = { onUpdate(form.copy(gallonsText = it)) }
    )
    SectionLabel("Receipt")
    ReceiptButtonSection(uiState = uiState, onEvent = onEvent)
    form.receiptError?.let { error ->
        Spacer(Modifier.height(4.dp))
        Text(
            text = error,
            color = MaterialTheme.colorScheme.error,
            style = MaterialTheme.typography.bodySmall
        )
    }
}

@Composable
private fun LoadingUnloadingFields(
    form: AddMileageFormState,
    locations: List<Location>,
    onUpdate: (AddMileageFormState) -> Unit,
    firstFieldFocus: FocusRequester,
    onSave: () -> Unit,
    locationError: String? = null,
    readOnly: Boolean = false
) {
    val focusManager = LocalFocusManager.current

    LocationPickerFields(
        form = form,
        locations = locations,
        nameFieldLabel = "Location name",
        onUpdate = onUpdate,
        locationError = locationError,
        readOnly = readOnly
    )
    SectionLabel("Notes")
    TimesheetTextField(
        modifier = Modifier.focusRequester(firstFieldFocus),
        text = form.loadingUnloadingNotesText,
        placeholder = "Notes",
        readOnly = readOnly,
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Sentences,
            imeAction = ImeAction.Done
        ),
        keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus(); onSave() }),
        onTextChange = { onUpdate(form.copy(loadingUnloadingNotesText = it)) }
    )
}

/**
 * Location selector for FUEL and LOADING/UNLOADING trips. Shows a read-only field that
 * opens [LocationSearchDialog]; both picking an existing location and entering a brand-new
 * one happen entirely inside that dialog, so the user never leaves the form to fill in
 * details. The `locations` list is expected to already be filtered to the relevant
 * [com.trevorwiebe.timesheet.mileage.domain.model.LocationType].
 */
@Composable
private fun LocationPickerFields(
    form: AddMileageFormState,
    locations: List<Location>,
    nameFieldLabel: String,
    onUpdate: (AddMileageFormState) -> Unit,
    locationError: String?,
    readOnly: Boolean = false,
    sectionLabel: String = "Location"
) {
    val focusManager = LocalFocusManager.current
    var showPicker by remember { mutableStateOf(false) }
    val selectedLocationName = when {
        form.isNewLocation && form.newLocationName.isNotEmpty() ->
            "${form.newLocationName} — ${form.newLocationCity}, ${form.newLocationState}"
        form.isNewLocation -> "New location…"
        else -> locations.firstOrNull { it.id == form.selectedLocationId }
            ?.let { "${it.name} — ${it.city}, ${it.state}" } ?: ""
    }

    SectionLabel(sectionLabel)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(4.dp))
            .border(1.dp, MaterialTheme.colorScheme.primary, RoundedCornerShape(4.dp))
            .clickable(enabled = !readOnly) {
                focusManager.clearFocus()
                showPicker = true
            }
            .padding(horizontal = 16.dp, vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = selectedLocationName.ifEmpty { "Select a location" },
            style = MaterialTheme.typography.bodyLarge,
            color = if (selectedLocationName.isEmpty()) {
                MaterialTheme.colorScheme.onSurfaceVariant
            } else {
                MaterialTheme.colorScheme.onBackground
            },
            modifier = Modifier.weight(1f)
        )
        Icon(
            imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }

    if (showPicker) {
        LocationSearchDialog(
            locations = locations,
            nameFieldLabel = nameFieldLabel,
            initialName = form.newLocationName,
            initialCity = form.newLocationCity,
            initialState = form.newLocationState,
            onSelect = { location ->
                onUpdate(form.copy(selectedLocationId = location.id, isNewLocation = false, locationError = null))
                showPicker = false
            },
            onNewLocation = { name, city, state ->
                onUpdate(
                    form.copy(
                        isNewLocation = true,
                        selectedLocationId = "",
                        newLocationName = name,
                        newLocationCity = city,
                        newLocationState = state,
                        locationError = null
                    )
                )
                showPicker = false
            },
            onDismiss = { showPicker = false }
        )
    }

    locationError?.let { error ->
        Spacer(Modifier.height(4.dp))
        Text(
            text = error,
            color = MaterialTheme.colorScheme.error,
            style = MaterialTheme.typography.bodySmall
        )
    }
}

/**
 * Full-screen searchable location picker. The search bar is pinned to the top so the
 * keyboard only ever covers the bottom of the results list (which the user scrolls/filters),
 * never the active input — unlike an inline dropdown anchored to a field near the
 * bottom of the form.
 *
 * Implemented per-platform because the dialog window needs `decorFitsSystemWindows = false`
 * (Android-only) plus IME soft-input handling; the shared UI lives in [LocationSearchContent].
 */
@Composable
internal expect fun LocationSearchDialog(
    locations: List<Location>,
    nameFieldLabel: String,
    initialName: String,
    initialCity: String,
    initialState: String,
    onSelect: (Location) -> Unit,
    onNewLocation: (name: String, city: String, state: String) -> Unit,
    onDismiss: () -> Unit,
    // Select-only mode for filters: hides the "New location…" affordance and offers a clear row
    // (e.g. "All Pickups") that routes to [onClear].
    allowNewLocation: Boolean = true,
    clearLabel: String? = null,
    onClear: (() -> Unit)? = null,
)

@Composable
internal fun LocationSearchContent(
    locations: List<Location>,
    nameFieldLabel: String,
    initialName: String,
    initialCity: String,
    initialState: String,
    onSelect: (Location) -> Unit,
    onNewLocation: (name: String, city: String, state: String) -> Unit,
    onDismiss: () -> Unit,
    allowNewLocation: Boolean = true,
    clearLabel: String? = null,
    onClear: (() -> Unit)? = null,
) {
    var query by remember { mutableStateOf("") }
    // Expand the inline new-location fields automatically when re-editing an entered one.
    var addingNew by remember { mutableStateOf(initialName.isNotEmpty()) }
    var name by remember { mutableStateOf(initialName) }
    var city by remember { mutableStateOf(initialCity) }
    var stateCode by remember { mutableStateOf(initialState) }
    var nameError by remember { mutableStateOf<String?>(null) }

    val cityFocus = remember { FocusRequester() }
    val stateFocus = remember { FocusRequester() }
    val focusManager = LocalFocusManager.current

    val filtered = if (query.isBlank()) {
        locations
    } else {
        locations.filter {
            it.name.contains(query, ignoreCase = true) ||
            it.city.contains(query, ignoreCase = true) ||
            it.state.contains(query, ignoreCase = true)
        }
    }

    fun submitNew() {
        if (name.isBlank()) {
            nameError = "Enter a $nameFieldLabel."
            return
        }
        focusManager.clearFocus()
        onNewLocation(name.trim(), city.trim(), stateCode.trim())
    }

    Surface(
        modifier = Modifier.fillMaxSize(),
        color = MaterialTheme.colorScheme.background
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
                .navigationBarsPadding()
                .imePadding()
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(
                    top = 8.dp,
                    bottom = 8.dp,
                    end = 16.dp
                ),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, contentDescription = "Close")
                }
                Text(
                    text = "Select Location",
                    style = MaterialTheme.typography.titleLarge
                )
            }
            Box(modifier = Modifier.padding(horizontal = 16.dp)) {
                TimesheetTextField(
                    text = query,
                    placeholder = "Search by name, city, or state",
                    onTextChange = { query = it }
                )
            }
            Spacer(Modifier.height(8.dp))
            if (allowNewLocation) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable {
                            addingNew = !addingNew
                            if (!addingNew) nameError = null
                        }
                        .padding(horizontal = 16.dp, vertical = 16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "New location…",
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.tertiary,
                        modifier = Modifier.weight(1f)
                    )
                    if (addingNew) {
                        Icon(
                            imageVector = Icons.Default.Close,
                            contentDescription = "Close new location",
                            tint = MaterialTheme.colorScheme.tertiary
                        )
                    }
                }
                if (addingNew) {
                    NewLocationFields(
                        nameFieldLabel = nameFieldLabel,
                        name = name,
                        city = city,
                        stateCode = stateCode,
                        nameError = nameError,
                        cityFocus = cityFocus,
                        stateFocus = stateFocus,
                        onNameChange = { name = it; nameError = null },
                        onCityChange = { city = it },
                        onStateChange = { stateCode = it },
                        onSubmit = { submitNew() }
                    )
                }
            }
            if (clearLabel != null) {
                Text(
                    text = clearLabel,
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.tertiary,
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onClear?.invoke() }
                        .padding(horizontal = 16.dp, vertical = 16.dp)
                )
            }
            HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))
            LazyColumn(modifier = Modifier.fillMaxWidth().weight(1f)) {
                items(filtered) { location ->
                    val cityState = listOf(location.city, location.state)
                        .filter { it.isNotBlank() }
                        .joinToString(", ")
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { onSelect(location) }
                            .padding(horizontal = 16.dp, vertical = 12.dp)
                    ) {
                        Text(
                            text = location.name,
                            style = MaterialTheme.typography.bodyLarge
                        )
                        if (cityState.isNotEmpty()) {
                            Text(
                                text = cityState,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                    HorizontalDivider(
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun NewLocationFields(
    nameFieldLabel: String,
    name: String,
    city: String,
    stateCode: String,
    nameError: String?,
    cityFocus: FocusRequester,
    stateFocus: FocusRequester,
    onNameChange: (String) -> Unit,
    onCityChange: (String) -> Unit,
    onStateChange: (String) -> Unit,
    onSubmit: () -> Unit
) {
    Column(modifier = Modifier.padding(horizontal = 16.dp).padding(bottom = 8.dp)) {
        TimesheetTextField(
            text = name,
            placeholder = nameFieldLabel,
            keyboardOptions = KeyboardOptions(
                imeAction = ImeAction.Next,
                capitalization = KeyboardCapitalization.Words
            ),
            keyboardActions = KeyboardActions(onNext = { cityFocus.requestFocus() }),
            onTextChange = onNameChange
        )
        nameError?.let { error ->
            Spacer(Modifier.height(4.dp))
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall
            )
        }
        Spacer(Modifier.height(8.dp))
        TimesheetTextField(
            modifier = Modifier.focusRequester(cityFocus),
            text = city,
            placeholder = "City",
            keyboardOptions = KeyboardOptions(
                imeAction = ImeAction.Next,
                capitalization = KeyboardCapitalization.Words
            ),
            keyboardActions = KeyboardActions(onNext = { stateFocus.requestFocus() }),
            onTextChange = onCityChange
        )
        Spacer(Modifier.height(8.dp))
        TimesheetTextField(
            modifier = Modifier.focusRequester(stateFocus),
            text = stateCode,
            placeholder = "State",
            keyboardOptions = KeyboardOptions(
                imeAction = ImeAction.Done,
                capitalization = KeyboardCapitalization.Characters
            ),
            keyboardActions = KeyboardActions(onDone = { onSubmit() }),
            onTextChange = onStateChange
        )
        Spacer(Modifier.height(12.dp))
        Button(
            onClick = onSubmit,
            modifier = Modifier.fillMaxWidth(),
            colors = ButtonDefaults.buttonColors(
                containerColor = MaterialTheme.colorScheme.tertiary,
                contentColor = MaterialTheme.colorScheme.onTertiary
            )
        ) {
            Text("Use this location")
        }
    }
}

@Composable
private fun ServiceFields(
    form: AddMileageFormState,
    onUpdate: (AddMileageFormState) -> Unit,
    firstFieldFocus: FocusRequester,
    onSave: () -> Unit,
    serviceLocationError: String? = null,
    readOnly: Boolean = false
) {
    val focusManager = LocalFocusManager.current

    SectionLabel("Service location")
    TimesheetTextField(
        modifier = Modifier.focusRequester(firstFieldFocus),
        text = form.serviceLocationText,
        placeholder = "Service location",
        readOnly = readOnly,
        keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
        keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus(); onSave() }),
        onTextChange = { onUpdate(form.copy(serviceLocationText = it, serviceLocationError = null)) }
    )
    serviceLocationError?.let { error ->
        Spacer(Modifier.height(4.dp))
        Text(
            text = error,
            color = MaterialTheme.colorScheme.error,
            style = MaterialTheme.typography.bodySmall
        )
    }
}

@Composable
private fun StartEndFields(
    form: AddMileageFormState,
    onUpdate: (AddMileageFormState) -> Unit,
    firstFieldFocus: FocusRequester,
    onSave: () -> Unit,
    readOnly: Boolean = false
) {
    val focusManager = LocalFocusManager.current

    SectionLabel("Location")
    TimesheetTextField(
        modifier = Modifier.focusRequester(firstFieldFocus),
        text = form.startEndLocationText,
        placeholder = "Location",
        readOnly = readOnly,
        keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
        keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus(); onSave() }),
        onTextChange = { onUpdate(form.copy(startEndLocationText = it)) }
    )
}

@Composable
private fun OtherFields(
    form: AddMileageFormState,
    locations: List<Location>,
    onUpdate: (AddMileageFormState) -> Unit,
    firstFieldFocus: FocusRequester,
    onSave: () -> Unit,
    readOnly: Boolean = false
) {
    val focusManager = LocalFocusManager.current

    LocationPickerFields(
        form = form,
        locations = locations,
        nameFieldLabel = "Location name",
        onUpdate = onUpdate,
        locationError = null,
        readOnly = readOnly,
        sectionLabel = "Location (optional)"
    )
    if (!readOnly && (form.selectedLocationId.isNotEmpty() || form.isNewLocation)) {
        TextButton(
            onClick = {
                onUpdate(
                    form.copy(
                        selectedLocationId = "",
                        isNewLocation = false,
                        newLocationName = "",
                        newLocationCity = "",
                        newLocationState = ""
                    )
                )
            },
            colors = ButtonDefaults.buttonColors(
                contentColor = MaterialTheme.colorScheme.tertiary,
                containerColor = MaterialTheme.colorScheme.background
            )
        ) {
            Text("Clear location")
        }
    }
    SectionLabel("Notes")
    TimesheetTextField(
        modifier = Modifier.focusRequester(firstFieldFocus),
        text = form.otherNotesText,
        placeholder = "Notes",
        readOnly = readOnly,
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Sentences,
            imeAction = ImeAction.Done
        ),
        keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus(); onSave() }),
        onTextChange = { onUpdate(form.copy(otherNotesText = it)) }
    )
}
